import { useEffect, useMemo, useState } from 'react'

import { getLettersHistory, getLettersStatuses } from './api'
import LoadingScreen from '../../common/LoadingScreen/components/LoadingScreen'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import TablePagination from '@mui/material/TablePagination'
import { Box, InputAdornment } from '@material-ui/core'
import moment from 'moment'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { Divider, IconButton } from '@mui/material'
import { SearchOutlined, ClearOutlined } from '@mui/icons-material'
import { translateToEnglish, translateToFrench } from './translations'
import { DateRangePicker } from './components/DateRangePicker'
import { LetterRow } from './components/LetterRow'
import { useDebounce } from '../../hooks/useDebounce'

export const LettersHistory = () => {
  const defaultRowsPerPage = 25
  const [letters, setLetters] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [statusOptions, setStatusOptions] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(moment().utc())

  useEffect(() => {
    getLettersStatuses().then((res) => {
      const statuses = res.map((status) => translateToFrench(status.name))
      const uniqueStatuses = [...new Set(statuses)]
      setStatusOptions(uniqueStatuses)
    })
  }, [])

  const translatedStatus = useMemo(() => {
    if (selectedStatus) {
      return translateToEnglish(selectedStatus)
    }
    return null
  }, [selectedStatus])

  const delay = 1000 // Adjust the delay as needed
  const debouncedSearchValue = useDebounce(searchValue, delay)

  const handleSearch = (e) => {
    setSearchValue(e.target.value)
  }

  const handleChangeStatus = (_, value) => {
    setSelectedStatus(value)
  }

  const handleClearValue = () => {
    setSearchValue('')
  }

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  useEffect(() => {
    if (debouncedSearchValue) {
      setPage(0)
    }
  }, [debouncedSearchValue])

  useEffect(() => {
    const convertToIsoDate = (date) => date.toISOString()
    getLettersHistory({
      page: page + 1,
      rowsPerPage,
      status: translatedStatus,
      searchValue: debouncedSearchValue,
      endDate: endDate && convertToIsoDate(endDate),
      startDate: startDate && convertToIsoDate(startDate),
    }).then((res) => {
      setLetters(res.rows)
      setTotalCount(res.count)
      setIsLoading(false)
    })
  }, [
    page,
    rowsPerPage,
    debouncedSearchValue,
    translatedStatus,
    endDate,
    startDate,
  ])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <Box width="100%">
      <Box
        sx={{
          marginY: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '30%',
          }}
        >
          <TextField
            sx={{ flex: 1, backgroundColor: 'white' }}
            placeholder="Filtrer par nom, copro ou adresse"
            inputProps={{ 'aria-label': 'search' }}
            value={searchValue}
            onChange={handleSearch}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ paddingRight: '0.625rem' }}
                >
                  {searchValue && (
                    <>
                      <Divider
                        sx={{ height: 28, mx: 0.5 }}
                        orientation="vertical"
                      />
                      <IconButton onClick={handleClearValue}>
                        <ClearOutlined />
                      </IconButton>
                    </>
                  )}
                  <IconButton disabled={true}>
                    <SearchOutlined />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '30%',
          }}
        >
          <DateRangePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </Box>
        <Autocomplete
          sx={{
            marginLeft: '20px',
            backgroundColor: 'white',
            width: '20%',
          }}
          disablePortal
          options={statusOptions}
          onChange={handleChangeStatus}
          renderInput={(params) => <TextField {...params} label="Statut" />}
          value={selectedStatus}
        />
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
              <TableCell>Copropriété</TableCell>
              <TableCell>Adresse</TableCell>
              <TableCell>Créé le</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Accusé</TableCell>
              <TableCell>Dépôt</TableCell>
              <TableCell>Document</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {letters &&
              letters.length > 0 &&
              letters.map((letter) => (
                <LetterRow key={letter.id} letter={letter} />
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Nombre par page"
        />
      </TableContainer>
    </Box>
  )
}
